import { clampRating, pickScenario } from './data';
import { timing, variantForLevel, } from './params';
export function makeStimulus(trialIdx, reappraiseMs, level, lang) {
    const variant = variantForLevel(level);
    const clampedDur = Math.max(6_000, Math.min(30_000, Math.round(reappraiseMs)));
    const scenario = pickScenario(lang, variant.complexity, trialIdx + 1);
    return {
        trialIdx,
        scenario,
        lang,
        reappraiseMs: clampedDur,
        targetDelta: variant.targetDelta,
    };
}
export function createCognitiveReappraisalAdapter(level = 1, lang = 'fr') {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
        ratingBefore: null,
        ratingAfter: null,
        strategy: null,
        remainingMs: 0,
    };
    void lang;
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    let reappraiseStart = 0;
    const timers = [];
    let tickInterval = null;
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
        if (tickInterval !== null) {
            clearInterval(tickInterval);
            tickInterval = null;
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(stim, ratingAfter) {
        if (!resolver)
            return;
        const before = state.ratingBefore ?? 5;
        const after = ratingAfter ?? before;
        const delta = before - after;
        const r = resolver;
        resolver = null;
        rejecter = null;
        clearTimers();
        r({
            ratingBefore: before,
            ratingAfter: after,
            strategy: state.strategy ?? 'none',
            delta,
            reactionTimeMs: Date.now() - trialStart,
            correct: delta >= stim.targetDelta,
        });
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'read',
                    stim,
                    feedback: null,
                    ratingBefore: null,
                    ratingAfter: null,
                    strategy: null,
                    remainingMs: stim.reappraiseMs,
                });
                trialStart = Date.now();
                timers.push(setTimeout(() => {
                    if (state.subPhase === 'read') {
                        update({ subPhase: 'before' });
                    }
                }, timing.readMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        finishRead() {
            if (state.subPhase !== 'read')
                return;
            update({ subPhase: 'before' });
        },
        submitBefore(rating) {
            if (state.subPhase !== 'before' || state.stim === null)
                return;
            const r = clampRating(rating);
            update({ ratingBefore: r, subPhase: 'reappraise' });
            reappraiseStart = Date.now();
            const stim = state.stim;
            tickInterval = setInterval(() => {
                const elapsed = Date.now() - reappraiseStart;
                const remaining = Math.max(0, stim.reappraiseMs - elapsed);
                if (remaining <= 0) {
                    if (tickInterval !== null) {
                        clearInterval(tickInterval);
                        tickInterval = null;
                    }
                    if (state.subPhase === 'reappraise') {
                        update({ remainingMs: 0, subPhase: 'after' });
                    }
                }
                else {
                    update({ remainingMs: remaining });
                }
            }, 250);
            timers.push(setTimeout(() => {
                if (state.subPhase === 'reappraise') {
                    update({ subPhase: 'after' });
                }
            }, stim.reappraiseMs + 100));
        },
        setStrategy(s) {
            if (state.subPhase !== 'reappraise')
                return;
            update({ strategy: s });
        },
        submitAfter(rating) {
            if ((state.subPhase !== 'after' && state.subPhase !== 'reappraise')
                || state.stim === null) {
                return;
            }
            const r = clampRating(rating);
            update({ ratingAfter: r, subPhase: 'after' });
            settle(state.stim, r);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map